import { ReactNode, useCallback, useMemo, useState, Children, useRef } from 'react';
import { contextFactory } from '@frontend/design-system';
import { WizardRenderer } from './stacked-card-wizard-renderer';

type WizardStepData = Record<string, unknown>;

type StepCallback = {
  stepIndex: number;
  callback: () => void | Promise<void>;
};

type StackedCardWizardContextType = {
  // Navigation state
  currentStep: number;
  totalSteps: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  canGoNext: boolean;
  canGoPrev: boolean;
  isLoading: boolean;

  // UI elements
  secondaryAction?: ReactNode;

  // Navigation actions
  nextStep: () => Promise<void>;
  prevStep: () => void;
  setCanGoNext: (canGoNext: boolean) => void;

  // Data management
  setStepData: (stepId: string, stepData: unknown) => void;
  getStepData: (stepId: string) => unknown;
  getAllData: () => WizardStepData;

  // Step title management
  setStepTitle: (stepIndex: number, title: ReactNode) => void;
  getStepTitle: (stepIndex: number) => ReactNode;

  // Step callback management
  registerStepCallback: (stepIndex: number, callback: () => void | Promise<void>) => void;
  unregisterStepCallback: (stepIndex: number) => void;
};

export const [StackedCardWizardContext, useStackedCardWizardContext] = contextFactory<StackedCardWizardContextType>(
  'useStackedCardWizardContext must be used within a StackedCardWizardProvider'
);

export type WizardCallbacks = {
  secondaryAction?: ReactNode;
  onStepChange?: (step: number, data: WizardStepData) => void;
  onComplete?: (data: WizardStepData) => void;
};

type StackedCardWizardProviderProps = {
  children: ReactNode;
} & WizardCallbacks;

export const StackedCardWizardProvider = ({
  children,
  secondaryAction,
  onStepChange: externalOnStepChange,
  onComplete: externalOnComplete,
}: StackedCardWizardProviderProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [canGoNext, setCanGoNext] = useState(true);
  const [data, setData] = useState<WizardStepData>({});
  const [stepCallbacks, setStepCallbacks] = useState<StepCallback[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Filter out falsy (conditional renders that evaluate to false) children
  const totalSteps = Children.toArray(children).filter(Boolean).length;

  const onStepChange = useCallback(
    (step: number, stepData: WizardStepData) => {
      externalOnStepChange?.(step, stepData);
    },
    [externalOnStepChange]
  );

  const onComplete = useCallback(
    (stepData: WizardStepData) => {
      externalOnComplete?.(stepData);
    },
    [externalOnComplete]
  );

  const nextStep = useCallback(async () => {
    setIsLoading(true);

    try {
      // Call the callback for the current step BEFORE moving to next
      const currentStepCallback = stepCallbacks.find((cb) => cb.stepIndex === currentStep);
      if (currentStepCallback) {
        await currentStepCallback.callback();
      }

      if (currentStep < totalSteps - 1) {
        const newStep = currentStep + 1;
        setCurrentStep(newStep);
        setCanGoNext(true);
        onStepChange?.(newStep, data);
      } else if (currentStep === totalSteps - 1) {
        setData((data: WizardStepData) => {
          onComplete?.(data);
          return data;
        });
      }
    } catch (error) {
      console.error('Error in step callback:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentStep, totalSteps, data, onStepChange, onComplete, stepCallbacks]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      const newStep = currentStep - 1;
      setCurrentStep(newStep);
      setCanGoNext(true);
      onStepChange?.(newStep, data);
    }
  }, [currentStep, data, onStepChange]);

  const setStepData = useCallback((stepId: string, stepData: unknown) => {
    setData((prevData: WizardStepData) => ({
      ...prevData,
      [stepId]: stepData,
    }));
  }, []);

  const getStepData = useCallback(
    (stepId: string): unknown => {
      return data[stepId];
    },
    [data]
  );

  const getAllData = useCallback((): WizardStepData => {
    return data;
  }, [data]);

  // useRefs for titles to avoid triggering re-renders when titles are set from child components
  const stepTitlesRef = useRef<Record<number, ReactNode>>({});

  const setStepTitle = useCallback((stepIndex: number, title: ReactNode) => {
    stepTitlesRef.current[stepIndex] = title;
  }, []);

  const getStepTitle = useCallback((stepIndex: number): ReactNode => {
    return stepTitlesRef.current[stepIndex] || `Step ${stepIndex + 1}`;
  }, []);

  const registerStepCallback = useCallback((stepIndex: number, callback: () => void | Promise<void>) => {
    setStepCallbacks((prev) => [...prev.filter((cb) => cb.stepIndex !== stepIndex), { stepIndex, callback }]);
  }, []);

  const unregisterStepCallback = useCallback((stepIndex: number) => {
    setStepCallbacks((prev) => prev.filter((cb) => cb.stepIndex !== stepIndex));
  }, []);

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;
  const canGoPrev = currentStep > 0;

  const value = useMemo(
    () => ({
      currentStep,
      totalSteps,
      isFirstStep,
      isLastStep,
      canGoNext,
      canGoPrev,
      isLoading,
      secondaryAction,
      nextStep,
      prevStep,
      setCanGoNext,
      setStepData,
      getStepData,
      getAllData,
      setStepTitle,
      getStepTitle,
      registerStepCallback,
      unregisterStepCallback,
    }),
    [
      currentStep,
      totalSteps,
      canGoNext,
      canGoPrev,
      isFirstStep,
      isLastStep,
      isLoading,
      secondaryAction,
      nextStep,
      prevStep,
      setStepData,
      getStepData,
      getAllData,
      setStepTitle,
      getStepTitle,
      registerStepCallback,
      unregisterStepCallback,
    ]
  );

  return (
    <StackedCardWizardContext.Provider value={value}>
      <WizardRenderer>{children}</WizardRenderer>
    </StackedCardWizardContext.Provider>
  );
};
